import { useEffect, useRef, useState } from "react";

function useRecursiveTimeout<T>(
  callback: () => Promise<T> | (() => void),
  timerDelay: number | null,
  isActivated: boolean
): { kill: () => void } {
  const savedCallback = useRef(callback);
  const [delay, setDelay] = useState(timerDelay);
  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout loop.
  useEffect(() => {
    let id: NodeJS.Timeout;
    function tick() {
      if (isActivated) {
        const ret = savedCallback.current();

        if (ret instanceof Promise) {
          ret.then(() => {
            if (isActivated && delay !== null) {
              id = setTimeout(tick, delay);
            }
          });
        } else {
          if (isActivated && delay !== null) {
            id = setTimeout(tick, delay);
          }
        }
      }
    }
    if (isActivated && delay !== null) {
      id = setTimeout(tick, delay);
      return () => id && clearTimeout(id);
    }
    if (!isActivated && id!) {
      clearTimeout(id);
    }
  }, [delay, isActivated]);
  const kill = () => {
    setDelay(null);
  };
  return { kill };
}

export default useRecursiveTimeout;
